import React from "react";
import { parseISO, format } from "date-fns";
import "./GameScheduleTable.css";

function GameScheduleTable({
  message,
  games,
  byes,
  displayRows,
  dateFilters,
  selectedGames,
  setSelectedGames,
  selectedByes,
  setSelectedByes,
  selectedWeekOffs,
  setSelectedWeekOffs,
  getGameDate,
  getGameTime,
  getTeamNameById,
  scoreMismatches,
  navigate,
}) {
  const getPlayerName = (player) => {
    if (!player) return "Unknown Player";

    return (
      player.name ||
      player.full_name ||
      `${player.firstName || ""} ${player.lastName || ""}`.trim() ||
      `${player.first_name || ""} ${player.last_name || ""}`.trim() ||
      player.username ||
      "Unknown Player"
    );
  };

  const getByeText = (entry) => {
    if (Array.isArray(entry.players) && entry.players.length > 0) {
      return `${entry.players.map(getPlayerName).join(", ")} (BYE)`;
    }

    if (Array.isArray(entry.teams) && entry.teams.length > 0) {
      return `${entry.teams
        .map((team) => {
          if (typeof team === "object") {
            return (
              team.name ||
              getTeamNameById(
                team.seasons_leagues_teams_id || team.id
              )
            );
          }

          return getTeamNameById(team);
        })
        .join(", ")} (BYE)`;
    }

    if (entry.team) {
      return `${entry.team.name ||
        getTeamNameById(
          entry.team.seasons_leagues_teams_id || entry.team.id
        )
        } (BYE)`;
    }

    if (entry.team_id) {
      return `${getTeamNameById(entry.team_id)} (BYE)`;
    }

    return "BYE";
  };

  const getMatchupText = (entry) => {
    if (entry.type === "bye") {
      return getByeText(entry);
    }

    if (entry.homePlayer || entry.awayPlayer) {
      const homeName =
        entry.match_format === "doubles"
          ? `${getPlayerName(entry.homePlayer)} / ${getPlayerName(
            entry.homePartnerPlayer
          )}`
          : getPlayerName(entry.homePlayer);

      const awayName =
        entry.match_format === "doubles"
          ? `${getPlayerName(entry.awayPlayer)} / ${getPlayerName(
            entry.awayPartnerPlayer
          )}`
          : getPlayerName(entry.awayPlayer);

      return `${homeName} vs ${awayName}`;
    }

    if (entry.homeTeam || entry.awayTeam) {
      return `${entry.homeTeam?.name || "Unknown Team"} vs ${entry.awayTeam?.name || "Unknown Team"
        }`;
    }

    return "Matchup TBD";
  };



  const getResultText = (entry) => {
    const isIndividual = entry.homePlayer || entry.awayPlayer;
    if (isIndividual) {
      const match = entry.racquetMatches?.[0];
      if (!match?.scores?.length) {
        return "—";
      }
      return match.scores
        .map((set) => `${set.home_score}-${set.away_score}`)
        .join(", ");
    }
    return `${entry.score_team1 ?? 0} - ${entry.score_team2 ?? 0}`;
  };



  return (
    <>
      {message && <p className="message">{message}</p>}

      <table className="game-schedule-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={
                  [...games, ...byes].length > 0 &&
                  [...games, ...byes]
                    .filter(
                      (entry) =>
                        dateFilters.length === 0 ||
                        dateFilters.includes(entry.date)
                    )
                    .every((entry) =>
                      entry.type === "bye"
                        ? selectedByes.includes(entry.id)
                        : selectedGames.includes(entry.id)
                    )
                }
                onChange={(e) => {
                  const filteredGames = games.filter(
                    (game) =>
                      dateFilters.length === 0 ||
                      dateFilters.includes(game.date)
                  );

                  const filteredByes = byes.filter(
                    (bye) =>
                      dateFilters.length === 0 ||
                      dateFilters.includes(bye.date)
                  );

                  if (e.target.checked) {
                    setSelectedGames(filteredGames.map((game) => game.id));
                    setSelectedByes(filteredByes.map((bye) => bye.id));
                  } else {
                    setSelectedGames([]);
                    setSelectedByes([]);
                  }
                }}
              />
            </th>

            <th>ID</th>
            <th>Date</th>
            <th>Matchup</th>
            <th>Location</th>
            <th>Time</th>
            <th>Status</th>
            <th>Results</th>
          </tr>
        </thead>

        <tbody>
          {displayRows.length === 0 ? (
            <tr>
              <td colSpan="8">
                No games, byes, or week-offs available
              </td>
            </tr>
          ) : (
            displayRows.map((entry, index) => {
              const isWeekOff = entry.type === "week_off";
              const isBye = entry.type === "bye";

              if (isWeekOff) {
                return (
                  <tr key={entry.id} className="week-off-row">
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedWeekOffs.includes(entry.id)}
                        onChange={() => {
                          setSelectedWeekOffs((prev) =>
                            prev.includes(entry.id)
                              ? prev.filter((id) => id !== entry.id)
                              : [...prev, entry.id]
                          );
                        }}
                      />
                    </td>

                    <td colSpan={7}>
                      🛑{" "}
                      <strong>
                        {format(
                          parseISO(entry.date),
                          "MMMM d, yyyy"
                        )}
                      </strong>{" "}
                      — No Games ({entry.reason || "Off Week"})
                    </td>
                  </tr>
                );
              }

              return (
                <tr
                  key={`${entry.type || "game"}-${entry.id}-${index}`}
                  className="clickable-row"
                  onClick={() => {
                    if (!isBye) {
                      navigate(`/dashboard/games/${entry.id}`);
                    }
                  }}
                  style={{
                    cursor: isBye ? "default" : "pointer",
                  }}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={
                        isBye
                          ? selectedByes.includes(entry.id)
                          : selectedGames.includes(entry.id)
                      }
                      onChange={() => {
                        if (isBye) {
                          setSelectedByes((prev) =>
                            prev.includes(entry.id)
                              ? prev.filter((id) => id !== entry.id)
                              : [...prev, entry.id]
                          );
                        } else {
                          setSelectedGames((prev) =>
                            prev.includes(entry.id)
                              ? prev.filter((id) => id !== entry.id)
                              : [...prev, entry.id]
                          );
                        }
                      }}
                    />
                  </td>

                  <td>{index + 1}</td>

                  <td>{getGameDate(entry)}</td>

                  <td>{getMatchupText(entry)}</td>

                  <td>
                    {isBye ? "—" : entry.location || "—"}
                  </td>

                  <td>
                    {isBye ? "—" : getGameTime(entry)}
                  </td>

                  <td>
                    {isBye
                      ? "Bye"
                      : entry.status
                        ? entry.status.charAt(0).toUpperCase() +
                        entry.status.slice(1)
                        : "Scheduled"}
                  </td>

                  <td>
                    {isBye ? (
                      "—"
                    ) : (
                      <>
                        {scoreMismatches[entry.id] && (
                          <span title="Mismatch: Results & Team Total">
                            {" "}
                            ⚠️
                          </span>
                        )}

                        {getResultText(entry)}
                      </>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </>
  );
}

export default GameScheduleTable;